import { X, Check } from 'lucide-react';
import React, { useState } from 'react';
import { WIDGET_CATEGORIES } from './types';
import { getWidgetsByScope, WIDGET_TYPES } from './widgetRegistry';

interface AddWidgetPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onAddWidget: (widgetType: string, size: '1x1' | '2x1' | '2x2') => void;
  scope: 'org' | 'project';
  existingWidgetTypes?: string[];
}

const AddWidgetPanel: React.FC<AddWidgetPanelProps> = ({
  isOpen,
  onClose,
  onAddWidget,
  scope,
  existingWidgetTypes = [],
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<Record<string, '1x1' | '2x1' | '2x2'>>({});

  if (!isOpen) return null;

  // Get widgets available for current scope
  const availableWidgets = getWidgetsByScope(scope);
  const categories = Object.values(WIDGET_CATEGORIES);

  const isWidgetAdded = (widgetId: string) => existingWidgetTypes.includes(widgetId);

  const handleWidgetSelect = (widgetId: string) => {
    if (isWidgetAdded(widgetId)) return; // Prevent adding duplicates

    const widget = WIDGET_TYPES.find(w => w.id === widgetId);
    if (widget) {
      const size = selectedSize[widgetId] || widget.defaultSize;
      onAddWidget(widgetId, size);
    }
  };

  const handleSizeSelect = (widgetId: string, size: '1x1' | '2x1' | '2x2', e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedSize(prev => ({ ...prev, [widgetId]: size }));
  };

  const getWidgetsForCategory = (category: string) => {
    return availableWidgets.filter(widget => widget.category === category);
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[85vh] flex flex-col shadow-2xl border border-gray-200">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-gray-200 bg-white rounded-t-2xl">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Add Widget</h2>
            <p className="text-sm text-gray-600 mt-1">
              Choose a widget to add to your {scope === 'org' ? 'organization' : 'project'}{' '}
              dashboard
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Categories Sidebar */}
          <div className="w-72 bg-gray-50/50 border-r border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Categories</h3>
            <nav className="space-y-2">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`w-full text-left px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  selectedCategory === null
                    ? 'bg-red-600 text-white shadow-sm'
                    : 'text-gray-700 hover:bg-white hover:shadow-sm'
                }`}
              >
                All Widgets ({availableWidgets.length})
              </button>
              {categories.map(category => {
                const widgetsInCategory = getWidgetsForCategory(category);
                if (widgetsInCategory.length === 0) return null;

                return (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`w-full text-left px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                      selectedCategory === category
                        ? 'bg-red-600 text-white shadow-sm'
                        : 'text-gray-700 hover:bg-white hover:shadow-sm'
                    }`}
                  >
                    {category} ({widgetsInCategory.length})
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Widgets Grid */}
          <div className="flex-1 p-8 overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(selectedCategory ? getWidgetsForCategory(selectedCategory) : availableWidgets).map(
                widget => {
                  const Icon = widget.icon;
                  const isAdded = isWidgetAdded(widget.id);
                  const currentSize = selectedSize[widget.id] || widget.defaultSize;

                  return (
                    <div
                      key={widget.id}
                      className={`relative text-left p-6 border rounded-2xl transition-all group ${
                        isAdded
                          ? 'border-green-300 bg-green-50/50 cursor-default'
                          : 'border-gray-200 hover:border-red-300 hover:shadow-lg bg-white hover:bg-red-50/30 cursor-pointer'
                      }`}
                      onClick={() => !isAdded && handleWidgetSelect(widget.id)}
                    >
                      {/* Added Badge */}
                      {isAdded && (
                        <div className="absolute top-3 right-3 flex items-center space-x-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                          <Check className="h-3 w-3" />
                          <span>Added</span>
                        </div>
                      )}

                      <div className="flex flex-col items-center text-center space-y-4">
                        <div
                          className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-colors ring-1 ${
                            isAdded
                              ? 'bg-green-100 ring-green-200'
                              : 'bg-red-50 ring-red-100 group-hover:bg-red-100'
                          }`}
                        >
                          <Icon
                            className={`h-8 w-8 ${isAdded ? 'text-green-600' : 'text-red-600'}`}
                          />
                        </div>
                        <div className="w-full">
                          <h4
                            className={`text-sm font-semibold mb-2 ${
                              isAdded ? 'text-green-700' : 'text-gray-900 group-hover:text-red-700'
                            }`}
                          >
                            {widget.name}
                          </h4>
                          <p className="text-xs text-gray-600 mb-4 leading-relaxed">
                            {widget.description}
                          </p>
                          <div className="flex items-center justify-center space-x-2">
                            <span className="text-xs text-gray-500 font-medium">Size:</span>
                            {widget.sizes.map(size => (
                              <button
                                key={size}
                                onClick={e => !isAdded && handleSizeSelect(widget.id, size, e)}
                                disabled={isAdded}
                                className={`text-xs px-3 py-1 rounded-full font-medium transition-colors ${
                                  isAdded
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : size === currentSize
                                      ? 'bg-red-500 text-white ring-1 ring-red-600'
                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                              >
                                {size}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
              )}
            </div>

            {/* Empty State */}
            {selectedCategory && getWidgetsForCategory(selectedCategory).length === 0 && (
              <div className="text-center py-12">
                <h3 className="text-heading-md text-gray-900 mb-2">No widgets in this category</h3>
                <p className="text-body-sm text-gray-500">
                  Try selecting a different category or view all widgets.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {scope === 'org'
                ? 'Organization widgets show data across all projects'
                : 'Project widgets show data for the current project only'}
            </div>
            <button
              onClick={onClose}
              className="px-6 py-2 text-sm font-medium text-gray-700 hover:bg-white hover:shadow-sm rounded-lg transition-colors border border-gray-200"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddWidgetPanel;
